import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { isCancelError } from './utils/axiosConfig';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Loader2, Bell, Search, LogOut, User } from 'lucide-react';
import PropertyCard from './components/PropertyCard';
import PropertyDetail from './components/PropertyDetail';
import AISearchNew from './components/AISearchNew';
import Login from './components/Login';
import Onboarding from './components/Onboarding';
import Profile from './components/Profile';
import HowToUse from './components/HowToUse';
import Pricing from './components/Pricing';
import { ToastContainer } from './components/Toast';
import { toast, subscribeToToasts } from './utils/toast';
import {
  auth,
  supabase,
  isSupabaseConfigured,
  getLocalAuthBootstrapHint,
  persistLocalAuthHint,
  clearLocalAuthHint,
} from './utils/auth';
import { userData } from './utils/userData';
import { getFxRate } from './utils/fxRate';
import brandLogo from './assets/logo.png';

const SESSION_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const SESSION_CHECK_GET_SESSION_MS = 3000;

function App() {
  // Read the bootstrap hint synchronously so the first render already knows
  // whether the user is logged in (avatar) or not ("Get started").
  const [initialAuthHint] = useState(() => getLocalAuthBootstrapHint());

  const BROWSE_DISPLAY_LIMIT = 300;
  const AI_PAGE_SIZE = 50;
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [view, setView] = useState('landing');
  const [fxRate, setFxRate] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    maxPrice: '',
    minSqm: '',
    rooms: '',
    furnished: ''
  });
  const [, setCities] = useState([]);
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [, setIsFromSignup] = useState(false);
  const [currentQuery, setCurrentQuery] = useState('');
  const [pendingQuery, setPendingQuery] = useState('');
  const [toasts, setToasts] = useState([]);
  const [currentPage, setCurrentPage] = useState(null); // 'how-to-use', 'pricing', etc.
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [profileInitialTab, setProfileInitialTab] = useState('profile');
  // isRestoringSession: true only while we await Supabase's INITIAL_SESSION
  // confirmation (the hint already handles the logged-in/out UI instantly).
  const [, setIsRestoringSession] = useState(true);
  // Navbar hint state — populated from localStorage on first paint, then kept
  // in sync with the real user object once Supabase confirms the session.
  const [navbarAuthState, setNavbarAuthState] = useState(
    initialAuthHint.hasLocalSessionHint ? 'logged_in_hint' : 'logged_out'
  );
  const [navbarUserPreview, setNavbarUserPreview] = useState(
    initialAuthHint.cachedUserPreview || null
  );
  const [failedAvatarSrcs, setFailedAvatarSrcs] = useState({});
  const [resultsSource, setResultsSource] = useState('browse'); // 'browse' | 'ai'
  const [aiPagination, setAiPagination] = useState({
    query: '',
    total: 0,
    hasMore: false,
    nextOffset: 0,
    pageSize: AI_PAGE_SIZE,
    thresholdDebug: null
  });
  const [isLoadingMoreAi, setIsLoadingMoreAi] = useState(false);
  const resultsSourceRef = useRef('browse'); // Ref so fetchProperties can read it without stale closure
  const dropdownRef = useRef(null);
  const restorationTimeoutRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);

  const getCityName = useCallback((cityField) => {
    if (!cityField) return '';
    if (typeof cityField === 'string') return cityField.trim();
    if (typeof cityField === 'object' && typeof cityField.city === 'string') return cityField.city.trim();
    return '';
  }, []);

  const normalizeLocation = useCallback((locationField, cityField) => {
    if (locationField && typeof locationField === 'object') {
      if (locationField.label || locationField.city) return locationField;
      const cityName = getCityName(cityField);
      if (cityName) return { ...locationField, city: cityName, label: cityName };
      return locationField;
    }
    const cityName = getCityName(cityField);
    return cityName ? { city: cityName, label: cityName } : null;
  }, [getCityName]);

  const mergeProfileIntoUser = useCallback((baseUser, profile, existsInDatabase = true) => {
    const authUserId = baseUser?.id || profile?.auth_user_id || null;
    const location = normalizeLocation(profile?.location, profile?.city);
    const profileImage =
      profile?.profile_image ||
      profile?.profileImage ||
      baseUser?.profileImage ||
      baseUser?.picture ||
      null;

    return {
      ...baseUser,
      ...profile,
      // Keep app-level id aligned to auth_user_id so route params stay stable.
      id: authUserId || profile?.id || null,
      auth_user_id: profile?.auth_user_id || authUserId,
      db_id: profile?.id || baseUser?.db_id || null,
      location,
      profileImage,
      profile_image: profileImage,
      name: profile?.name || baseUser?.name,
      profileCompleted: !!profile?.profile_completed,
      existsInDatabase
    };
  }, [normalizeLocation]);

  useEffect(() => {
    getFxRate().then((r) => r && setFxRate(r)).catch(() => {});
  }, []);

  // Keep ref in sync so callbacks can read current resultsSource without stale closure
  useEffect(() => {
    resultsSourceRef.current = resultsSource;
  }, [resultsSource]);

  const buildUserFromSession = useCallback(async (supabaseUser) => {
    const baseUser = auth.toAppUser(supabaseUser);
    if (!baseUser) return null;

    try {
      const profile = await userData.getProfile(baseUser.id);

      if (profile) {
        return mergeProfileIntoUser(baseUser, profile, true);
      }

      // User not found in DB — create the row now.
      // This runs after onAuthStateChange so the cached Bearer token is available.
      try {
        const createRes = await api.post('/api/users', {
          auth_user_id: baseUser.id,
          email: baseUser.email,
          name: baseUser.name,
          auth_provider: baseUser.authProvider || 'email'
        });
        const created = createRes?.data?.user;
        if (created) {
          return mergeProfileIntoUser(baseUser, created, true);
        }
      } catch (createError) {
        console.error('Failed to create user in database:', createError);
      }

      // Creation failed or returned no data — let onboarding handle it
      return { ...baseUser, existsInDatabase: false, profileCompleted: false };
    } catch (error) {
      console.error('Unexpected error fetching user profile:', error);
      return { ...baseUser, existsInDatabase: true, profileCompleted: false };
    }
  }, [mergeProfileIntoUser]);

  const fetchProperties = useCallback(async (signal, showLoading = false) => {
    if (showLoading) {
      setLoadingProperties(true);
    }
    try {
      const response = await api.get('/api/properties', { signal });
      const limited = Array.isArray(response.data) ? response.data.slice(0, BROWSE_DISPLAY_LIMIT) : [];
      if (!signal?.aborted) {
        setProperties(limited);
        // Don't overwrite AI search results when a background fetch completes
        if (resultsSourceRef.current !== 'ai') {
          setFilteredProperties(limited);
          setResultsSource('browse');
        }
      }
    } catch (error) {
      // Ignore AbortError - request was cancelled intentionally
      if (isCancelError(error)) {
        return;
      }
      console.error('Error fetching properties:', error);
    } finally {
      if (!signal?.aborted && showLoading) {
        setLoadingProperties(false);
      }
    }
  }, []);

  const fetchCities = useCallback(async (signal) => {
    try {
      const response = await api.get('/api/cities', { signal });
      if (!signal?.aborted) {
        setCities(response.data);
      }
    } catch (error) {
      // Ignore AbortError - request was cancelled intentionally
      if (isCancelError(error)) {
        return;
      }
      console.error('Error fetching cities:', error);
    }
  }, []);

  // Keep the navbar preview in sync and persist the hint for the next page load.
  const setNavbarFromUser = useCallback((nextUser) => {
    if (!nextUser) return;
    const preview = {
      id: nextUser.id || null,
      name: nextUser.name || 'User',
      email: nextUser.email || '',
      profileImage: nextUser.profileImage || nextUser.profile_image || nextUser.picture || null,
    };
    persistLocalAuthHint(preview);
    setNavbarUserPreview(preview);
    setNavbarAuthState('logged_in_hint');
  }, []);

  const clearNavbarAuthState = useCallback(() => {
    clearLocalAuthHint();
    setNavbarUserPreview(null);
    setNavbarAuthState('logged_out');
    setFailedAvatarSrcs({});
  }, []);

  const applyFilters = useCallback(async (signal) => {
    if (!user) return;
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      if (filters.maxPrice) {
        params.append('currency', user?.currency_preference || 'EUR');
      }

      const response = await api.get(`/api/properties?${params.toString()}`, { signal });
      const limited = Array.isArray(response.data) ? response.data.slice(0, BROWSE_DISPLAY_LIMIT) : [];
      setFilteredProperties(limited);
    } catch (error) {
      // Ignore AbortError - request was cancelled intentionally
      if (isCancelError(error)) {
        return;
      }
      console.error('Error applying filters:', error);
    }
  }, [filters, user]);

  useEffect(() => {
    let isMounted = true;
    let authSubscription = null;

    // Clear any previous timeout (e.g. from Strict Mode re-run)
    if (restorationTimeoutRef.current) {
      clearTimeout(restorationTimeoutRef.current);
      restorationTimeoutRef.current = null;
    }

    // Load properties and cities immediately - don't wait for auth
    const abortController = new AbortController();
    fetchProperties(abortController.signal, true);
    fetchCities(abortController.signal);

    if (isMounted) {
      setInitialLoad(false);
    }

    const clearSessionRestoreTimeout = () => {
      if (restorationTimeoutRef.current) {
        clearTimeout(restorationTimeoutRef.current);
        restorationTimeoutRef.current = null;
      }
    };

    // Safety valve: if INITIAL_SESSION never fires (network failure, cold start),
    // unblock isRestoringSession after 5 s so the UI never shows a permanent spinner.
    // Do NOT clear the auth hint here — Supabase may still be refreshing the token
    // and we have no evidence the user is logged out.
    restorationTimeoutRef.current = setTimeout(() => {
      if (!isMounted) return;
      console.warn('Session restore timeout reached, proceeding with current hint state.');
      setIsRestoringSession(false);
    }, 5000);

    if (!isSupabaseConfigured || !supabase) {
      clearSessionRestoreTimeout();
      if (isMounted) {
        setIsRestoringSession(false);
      }
    } else {

    // Auth state: use onAuthStateChange as the single source of truth for isRestoringSession.
    // INITIAL_SESSION fires on every page load (with or without a session) — it is our signal that
    // Supabase has finished checking storage/URL hash. SIGNED_IN fires after OAuth redirect.
      const { data } = auth.onAuthStateChange(async (event, session) => {
        console.log('🔐 Auth state changed:', event);

        if (event === 'SIGNED_OUT' && isMounted) {
          setUser(null);
          clearNavbarAuthState();
          setShowLogin(false);
          setShowOnboarding(false);
          setIsFromSignup(false);
          setView('landing');
          clearSessionRestoreTimeout();
          setIsRestoringSession(false);
          return;
        }

        if (event === 'TOKEN_REFRESHED' && session?.access_token && isMounted && session?.user) {
          // Keep the navbar preview fresh after a silent token refresh.
          setNavbarFromUser(auth.toAppUser(session.user));
          return;
        }

        if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN') && isMounted) {
          if (session?.access_token) {
            // Optimistically update the navbar with basic Supabase user data
            // while the fuller profile fetch is in flight.
            setNavbarFromUser(auth.toAppUser(session.user));
            try {
              const pathname = window.location.pathname || '/';
              const search = window.location.search || '';
              window.history.replaceState(null, '', pathname + search);
              const appUser = await buildUserFromSession(session.user);
              if (!isMounted) return;
              if (appUser) {
                setUser(appUser);
                setNavbarFromUser(appUser);
                setShowLogin(false);
                if (!appUser.existsInDatabase) {
                  setShowOnboarding(true);
                  setIsFromSignup(!appUser.existsInDatabase);
                } else {
                  setShowOnboarding(false);
                  setIsFromSignup(false);
                }
                setView('landing');
              }
            } catch (err) {
              console.error('Session build failed:', err);
            }
          } else {
            // Supabase confirmed: no valid session. Clear any stale hint.
            if (isMounted) clearNavbarAuthState();
          }
          // Always clear the loading state once Supabase has resolved the session
          clearSessionRestoreTimeout();
          if (isMounted) setIsRestoringSession(false);
        }
      });
      authSubscription = data?.subscription || null;
    }

    // Subscribe to toast notifications - only keep the most recent toast
    const unsubscribe = subscribeToToasts((newToast) => {
      // Replace current toast with new one (single toast at a time)
      setToasts([newToast]);
    });

    return () => {
      isMounted = false;
      abortController.abort();
      unsubscribe();
      if (restorationTimeoutRef.current) {
        clearTimeout(restorationTimeoutRef.current);
        restorationTimeoutRef.current = null;
      }
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, [fetchProperties, fetchCities, clearNavbarAuthState, setNavbarFromUser, buildUserFromSession]);

  useEffect(() => {
    // Only refetch properties if user logs in (they're already loaded on initial mount)
    if (!user || initialLoad) return;
    
    const abortController = new AbortController();
    
    fetchProperties(abortController.signal, false);
    fetchCities(abortController.signal);
    
    return () => {
      abortController.abort();
    };
  }, [user, fetchProperties, fetchCities, initialLoad]);

  // Keep navbar hint in sync whenever the real user object changes.
  useEffect(() => {
    if (user) setNavbarFromUser(user);
  }, [user, setNavbarFromUser]);

  // Scroll to top when page/view changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view, currentPage, showProfile]);

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  useEffect(() => {
    if (view !== 'app' || !user) return;
    if (resultsSource === 'ai') return;
    
    const abortController = new AbortController();
    applyFilters(abortController.signal);
    
    return () => {
      abortController.abort();
    };
  }, [filters, view, user, applyFilters, resultsSource]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  // Periodic and on-focus session check (auth persistence disabled: no cookie; user is never set from auth so this rarely runs)
  useEffect(() => {
    if (!user || !supabase) return;

    const isSessionValid = (session) => {
      if (!session?.access_token) return false;
      const exp = session.expires_at ? session.expires_at * 1000 : null;
      return !exp || exp > Date.now();
    };

    const runSessionCheck = async () => {
      let supabaseValid = false;
      try {
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('GET_SESSION_TIMEOUT')), SESSION_CHECK_GET_SESSION_MS);
        });
        const result = await Promise.race([sessionPromise, timeoutPromise]);
        const session = result?.data?.session;
        supabaseValid = session && isSessionValid(session);
        if (supabaseValid) return;
      } catch {
        // getSession timed out or threw
      }
      // Supabase uses noPersistStorage so getSession() is often empty; session is restored via server cookie.
      // Only expire if the server also says we're not authenticated.
      try {
        const meRes = await api.get('/api/auth/me');
        if (meRes?.status === 200 && meRes?.data?.user) return;
      } catch {
        // 401 or network: server says not authenticated
      }
      setUser(null);
      toast.error('Your session expired. Please sign in again.', 'Session Expired');
    };

    const intervalId = setInterval(runSessionCheck, SESSION_CHECK_INTERVAL_MS);
    const onFocus = () => runSessionCheck();
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [user]);

  const clearFilters = () => {
    if (resultsSource !== 'browse') setResultsSource('browse');
    setFilters({
      search: '',
      city: '',
      maxPrice: '',
      minSqm: '',
      rooms: '',
      furnished: ''
    });
    setFilteredProperties(properties.slice(0, BROWSE_DISPLAY_LIMIT));
  };

  const handleAISearchResults = (payload, query) => {
    const normalizedPayload = Array.isArray(payload) ? { properties: payload } : (payload || {});
    const results = Array.isArray(normalizedPayload.properties) ? normalizedPayload.properties : [];
    const total = Number.isFinite(normalizedPayload.total) ? normalizedPayload.total : results.length;
    const hasMore = !!normalizedPayload.hasMore;
    const nextOffset = Number.isFinite(normalizedPayload.nextOffset) ? normalizedPayload.nextOffset : results.length;
    const pageSize = Number.isFinite(normalizedPayload.limit) && normalizedPayload.limit > 0 ? normalizedPayload.limit : AI_PAGE_SIZE;

    setFilteredProperties(results);
    setResultsSource('ai');
    setView('app');
    setPendingQuery('');
    setAiPagination({
      query,
      total,
      hasMore,
      nextOffset,
      pageSize,
      thresholdDebug: normalizedPayload.thresholdDebug || null
    });

    // Save search history to database (fire-and-forget; toast on failure)
    if (user) {
      userData.saveSearch(user.id, query, results, filters).catch(() => {
        toast.error('Search could not be saved to history.', 'Save Failed');
      });
    }
  };

  const loadMoreAISearchResults = useCallback(async () => {
    if (resultsSource !== 'ai') return;
    if (isLoadingMoreAi || !aiPagination.hasMore || !aiPagination.query) return;

    setIsLoadingMoreAi(true);
    try {
      const response = await api.post('/api/ai-search', {
        query: aiPagination.query,
        offset: aiPagination.nextOffset,
        limit: aiPagination.pageSize
      });
      const incoming = Array.isArray(response?.data?.properties) ? response.data.properties : [];
      setFilteredProperties(prev => {
        const seen = new Set(prev.map(p => p?.id || p?.pineconeId).filter(Boolean));
        const dedupedIncoming = incoming.filter(p => {
          const key = p?.id || p?.pineconeId;
          if (!key) return true;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        return [...prev, ...dedupedIncoming];
      });
      setAiPagination(prev => ({
        ...prev,
        total: Number.isFinite(response?.data?.total) ? response.data.total : prev.total,
        hasMore: !!response?.data?.hasMore,
        nextOffset: Number.isFinite(response?.data?.nextOffset) ? response.data.nextOffset : prev.nextOffset,
        thresholdDebug: response?.data?.thresholdDebug ?? prev.thresholdDebug
      }));
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
      console.error('Error loading more AI search results:', error);
      toast.error('Could not load more properties. Please try again.', 'Load More Failed');
    } finally {
      setIsLoadingMoreAi(false);
    }
  }, [aiPagination, isLoadingMoreAi, resultsSource]);

  useEffect(() => {
    if (view !== 'app' || resultsSource !== 'ai' || !aiPagination.hasMore) return;
    const target = loadMoreTriggerRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          loadMoreAISearchResults();
        }
      },
      { root: null, rootMargin: '300px 0px', threshold: 0.1 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [view, resultsSource, aiPagination.hasMore, loadMoreAISearchResults, filteredProperties.length]);

  const handleLogin = async (email, password) => {
    try {
      const authData = await auth.signInWithEmail(email, password);

      if (!authData.user) {
        throw new Error('Login failed. Please try again.');
      }

      // Verify user exists in database
      try {
        const response = await api.get(`/api/users/${authData.user.id}`);
        if (!response.data.user) {
          throw new Error('Account not found. Please sign up.');
        }
      } catch (dbError) {
        if (dbError.response?.status === 404) {
          throw new Error('Account not found. Please sign up.');
        }
        throw dbError;
      }

      // Set session cookie and app user (same flow as OAuth)
      const session = await auth.getSession();
      if (session?.access_token) {
        await api.post('/api/auth/session', { access_token: session.access_token });
        const appUser = await buildUserFromSession(authData.user);
        if (appUser) setUser(appUser);
      }
      setShowLogin(false);
      toast.success('Welcome back!', 'Login Successful');
    } catch (error) {
      // Check for specific Supabase error codes
      if (error.message?.includes('Invalid login credentials') ||
          error.message?.includes('Invalid password') ||
          error.code === 'invalid_credentials') {
        toast.error('Invalid password. Please try again.', 'Login Failed');
      } else if (error.message?.includes('Account not found')) {
        toast.error('Account not found. Please sign up.', 'Login Failed');
      } else {
        toast.error(error.message || 'Login failed. Please try again.', 'Login Failed');
      }
    }
  };

  const handleSignup = async (name, email, password) => {
    try {
      const authData = await auth.signUpWithEmail(name, email, password);
      
      if (!authData.user) {
        throw new Error('Signup failed. Please try again.');
      }

      // DB row creation is handled by buildUserFromSession when onAuthStateChange fires.
      setShowLogin(false);
      toast.success('Account created successfully!', 'Welcome to GrotN');
    } catch (error) {
      toast.error(error.message || 'Signup failed. Please try again.', 'Signup Failed');
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await auth.signInWithGoogle();
      // Note: Google OAuth redirects, so user creation will be handled in auth state change handler
    } catch (error) {
      toast.error(error.message || 'Google sign-in failed. Please try again.', 'Google Sign-In Failed');
    }
  };

  const handleLogout = async () => {
    clearNavbarAuthState();
    setUser(null);
    setShowLogin(false);
    setShowOnboarding(false);
    setShowProfile(false);
    setCurrentPage(null);
    setPendingQuery('');
    setShowUserDropdown(false);
    setView('landing');
    try {
      await api.post('/api/auth/logout');
    } catch (e) {
      // Ignore logout API errors (cookie may already be cleared)
    }
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Navigation handlers for dropdown menu
  const handleNavigateToProfile = (tab) => {
    setProfileInitialTab(tab);
    setShowProfile(true);
    setCurrentPage(null);
    setShowUserDropdown(false);
  };

  // Get user avatar source (supports profileImage, profile_image from API, and picture)
  const getAvatarSrc = (user) => {
    const src = user?.profileImage ?? user?.profile_image ?? user?.picture ?? null;
    if (!src) return null;
    if (typeof src === 'string' && src.startsWith('emoji:')) return src;
    if (failedAvatarSrcs[src]) return null;
    return src;
  };

  const markAvatarSrcFailed = (src) => {
    if (!src || typeof src !== 'string') return;
    setFailedAvatarSrcs((prev) => (prev[src] ? prev : { ...prev, [src]: true }));
  };

  // Check if avatar is emoji
  const isEmojiAvatar = (src) => {
    return src && typeof src === 'string' && src.startsWith('emoji:');
  };

  // Get user initials for fallback avatar
  const getUserInitials = (user) => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return user.name[0].toUpperCase();
  };

  // navbarUser: the real user once Supabase confirms, or the cached preview
  // during the hint phase. "Get started" only shows when this is null.
  const navbarUser = user || (navbarAuthState === 'logged_in_hint' ? navbarUserPreview : null);
  const canOpenUserMenu = Boolean(navbarUser);

  const handleSaveScout = useCallback(async (scoutData) => {
    console.log('[Scout] handleSaveScout called', { name: scoutData?.name, hasUser: !!user });

    if (!user) {
      toast.info('Create an account to save scouts and get email alerts', 'Sign Up Required');
      setShowLogin(true);
      throw new Error('No user');
    }

    const filters = scoutData.filters || {};
    if (filters.maxPrice && user?.currency_preference) {
      filters.currency = user.currency_preference;
    }
    const payload = {
      name: scoutData.name,
      query: scoutData.query || '',
      filters
    };
    const postUrl = (api.defaults.baseURL || '') + '/api/scouts';
    console.log('[Scout] Sending POST', postUrl, payload);

    try {
      const response = await api.post('/api/scouts', payload);
      console.log('[Scout] POST /api/scouts success', response?.status, response?.data);

      // Log response details
      console.log('[Scout] Response details:', {
        status: response?.status,
        statusText: response?.statusText,
        data: response?.data,
        scout: response?.data?.scout,
        remaining: response?.data?.remaining
      });
      
      // Show single success toast
      toast.success(
        `Your current search "${scoutData.name}" has been saved. You'll receive email alerts when we find matching properties.`,
        'Scout Saved',
        5000
      );

      // Take user to search results view so they see their results
      setView('app');
    } catch (error) {
      console.error('[Scout] Error saving scout:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
        fullError: error
      });
      const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message || 'Error saving scout. Please try again.';
      toast.error(errorMsg, 'Error Saving Scout');
      throw error; // rethrow so caller (AISearchNew) can keep form open
    }
  }, [user]);

  const handleOnboardingComplete = async (profileData) => {
    try {
      console.log('handleOnboardingComplete called with profileData:', profileData);
      console.log('Current user:', user);
      
      if (!user) {
        console.error('No user found when completing onboarding');
        toast.error('User session not found. Please log in again.', 'Error');
        return;
      }
      
      // Save full profile according to schema
      const fullProfile = {
        firstname: profileData.firstname || user.name?.split(' ')[0] || '',
        lastname: profileData.lastname || user.name?.split(' ').slice(1).join(' ') || '',
        email: profileData.email || user.email,
        defaultRole: profileData.defaultRole || 'TENANT',
        signupType: profileData.signupType || (user.authProvider === 'google' ? 'GOOGLE' : 'EMAIL'),
        isNewUser: false,
        gender: profileData.gender || '',
        dob: profileData.dob || '',
        location: profileData.location || null,
        budget: profileData.budget || { min: profileData.maxBudget ? '' : '', max: profileData.maxBudget || '' },
        currency_preference: profileData.currency_preference || 'USD',
        area_unit_preference: profileData.area_unit_preference || 'sqm',
        profileImage: profileData.profileImage || user.picture || null,
        profileCompleted: true,
        createdAt: profileData.createdAt || user.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      console.log('Full profile to save:', fullProfile);
      
      // Update user in database via API
      try {
        let userId = user.auth_user_id || user.id;
        if (!user.auth_user_id && supabase) {
          try {
            const { data: sessionData } = await supabase.auth.getSession();
            if (sessionData?.session?.user?.id) {
              userId = sessionData.session.user.id;
            }
          } catch {}
        }

        console.log('Updating user in database via API...');
        // Prepare update data according to database schema
        const updateData = {
          name: `${fullProfile.firstname} ${fullProfile.lastname}`.trim() || user.name,
          profile_completed: true
        };

        // Only add fields that have values (all jsonb fields should be objects)
        if (fullProfile.location && typeof fullProfile.location === 'object' && Object.keys(fullProfile.location).length > 0) {
          updateData.location = fullProfile.location;
          // city field can store the city from location object if needed
          if (fullProfile.location.city) {
            updateData.city = { city: fullProfile.location.city };
          }
        }
        if (fullProfile.budget && typeof fullProfile.budget === 'object' && (fullProfile.budget.min || fullProfile.budget.max)) {
          updateData.budget = {
            min: fullProfile.budget.min || null,
            max: fullProfile.budget.max || null
          };
          updateData.budget_currency = fullProfile.currency_preference || 'USD';
        }
        if (fullProfile.gender?.trim()) {
          updateData.gender = fullProfile.gender.trim();
        }
        if (fullProfile.dob) {
          updateData.dob = fullProfile.dob;
        }
        if (fullProfile.profileImage) {
          updateData.profile_image = fullProfile.profileImage;
        }
        if (fullProfile.currency_preference) {
          updateData.currency_preference = fullProfile.currency_preference;
        }
        if (fullProfile.area_unit_preference) {
          updateData.area_unit_preference = fullProfile.area_unit_preference;
        }
        if (profileData.country) {
          updateData.country = profileData.country;
        }
        if (profileData.state_code) {
          updateData.state_code = profileData.state_code;
        }

        await api.patch(`/api/users/${userId}`, updateData);
        console.log('User updated successfully in database');
        
        // Fetch updated user from API to get complete profile
        const updatedUserResponse = await api.get(`/api/users/${userId}`);
        const updatedUserData = updatedUserResponse.data?.user;
        
        if (updatedUserData) {
          setUser(prev => (prev ? mergeProfileIntoUser(prev, updatedUserData, true) : prev));
        } else {
          // Fallback if API fetch fails
          setUser(prev => ({
            ...prev,
            name: `${fullProfile.firstname} ${fullProfile.lastname}`.trim() || prev.name,
            location: fullProfile.location,
            profileCompleted: true
          }));
        }
      } catch (error) {
        console.error('Error updating user in database:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        toast.error('Failed to save profile. Please try again.', 'Error');
        return;
      }
      
      console.log('Closing onboarding modal');
      setIsFromSignup(false);
      setShowOnboarding(false);
      setView('landing');
      toast.success('Profile setup complete!', 'Welcome to GrotN');
    } catch (error) {
      console.error('Error in handleOnboardingComplete:', error);
      toast.error('Failed to complete profile setup. Please try again.', 'Error');
    }
  };

  const handleOnboardingSkip = () => {
    // Skip onboarding without saving any data
    console.log('Skipping onboarding - no data will be saved');
    setIsFromSignup(false);
    setShowOnboarding(false);
    setView('landing');
    // Don't show toast - user chose to skip
  };

  const handleAIFiltersApplied = (aiFilters) => {
    setFilters(prev => ({
      ...prev,
      city: aiFilters.city || '',
      maxPrice: aiFilters.maxPrice || '',
      minSqm: aiFilters.minSqm || '',
      rooms: aiFilters.rooms || '',
      furnished: aiFilters.furnished || '',
      search: aiFilters.search || ''
    }));
  };

  // Show UI immediately - don't block on loading
  // Only show a minimal loading indicator if absolutely necessary

  if (selectedProperty) {
    // If not logged in, show signup prompt instead of property details
    if (!user) {
      return (
        <div className="min-h-screen bg-brand-surface flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl text-center"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Create an account to view details</h2>
            <p className="text-gray-600 mb-6">
              Sign up to see full property details, AI-generated summaries, and save your favorite listings.
            </p>
            <div className="space-y-3">
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedProperty(null);
                  setShowLogin(true);
                }}
                className="w-full px-6 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
              >
                Sign Up Now
              </motion.button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedProperty(null);
                }}
                className="w-full px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
              >
                Back to search
              </button>
            </div>
          </motion.div>
        </div>
      );
    }
    
    return (
      <PropertyDetail
        property={selectedProperty}
        onBack={() => setSelectedProperty(null)}
        userQuery={currentQuery}
        currencyPreference={user?.currency_preference}
        areaUnitPreference={user?.area_unit_preference}
        fxRate={fxRate}
      />
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen bg-brand-surface font-sans selection:bg-[#9c795b]/20 text-brand-primary">
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      
      {showLogin && (
        <Login
          onLogin={handleLogin}
          onSignup={handleSignup}
          onGoogleLogin={handleGoogleLogin}
          onClose={() => setShowLogin(false)}
        />
      )}

      {/* Navigation */}
      <nav className="fixed w-full top-0 z-50 bg-brand-surface/80 backdrop-blur-xl border-b border-[#9c795b]/10">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={(e) => {
              e.preventDefault();
              window.location.href = '/';
            }}
            className="flex items-center gap-3 cursor-pointer"
          >
            <img src={brandLogo} alt="GrotN" className="h-7 w-auto" />
            <div className="flex flex-col">
              <span className="font-semibold text-lg tracking-tight text-gray-900">GrotN</span>
              <span className="text-xs text-gray-500 -mt-1">Your AI rental searcher</span>
            </div>
          </motion.div>

          {/* Navigation Links — shown only when we know the user is logged out */}
          <div className="hidden sm:flex items-center gap-4 md:gap-6">
            {navbarAuthState === 'logged_out' && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage('how-to-use');
                    setShowProfile(false);
                  }}
                  className="text-xs sm:text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                >
                  How it works
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage('pricing');
                    setShowProfile(false);
                  }}
                  className="text-xs sm:text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                >
                  Pricing
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-4">
            {navbarUser ? (
              // Avatar — shown instantly from the localStorage hint or the real user.
              // Dropdown is only interactive once Supabase has confirmed the session.
              <div className="relative" ref={dropdownRef}>
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (canOpenUserMenu) setShowUserDropdown(!showUserDropdown);
                  }}
                  className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 rounded-full"
                >
                  {getAvatarSrc(navbarUser) && !isEmojiAvatar(getAvatarSrc(navbarUser)) ? (
                    <img
                      src={getAvatarSrc(navbarUser)}
                      alt={navbarUser.name}
                      className="w-10 h-10 rounded-full border-2 border-gray-200 hover:border-gray-300 transition-colors object-cover"
                      onError={(e) => markAvatarSrcFailed(e.currentTarget.currentSrc || e.currentTarget.src || getAvatarSrc(navbarUser))}
                    />
                  ) : null}
                  <div
                    className={`w-10 h-10 rounded-full border-2 border-gray-200 hover:border-gray-300 transition-colors flex items-center justify-center font-semibold text-gray-700 bg-gray-100 ${
                      getAvatarSrc(navbarUser) && !isEmojiAvatar(getAvatarSrc(navbarUser)) ? 'hidden' : ''
                    } ${isEmojiAvatar(getAvatarSrc(navbarUser)) ? 'text-xl' : 'text-sm'}`}
                  >
                    {isEmojiAvatar(getAvatarSrc(navbarUser))
                      ? getAvatarSrc(navbarUser).replace('emoji:', '')
                      : getUserInitials(navbarUser)}
                  </div>
                </motion.button>

                <AnimatePresence>
                  {showUserDropdown && navbarUser && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                    >
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">{navbarUser.name}</p>
                        <p className="text-xs text-gray-500 truncate">{navbarUser.email}</p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavigateToProfile('profile');
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3"
                      >
                        <User className="w-4 h-4" />
                        <span>Profile</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavigateToProfile('scouts');
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3"
                      >
                        <Bell className="w-4 h-4" />
                        <span>My Scouts</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavigateToProfile('searches');
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3"
                      >
                        <Search className="w-4 h-4" />
                        <span>Search History</span>
                      </button>
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleLogout();
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-3"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.preventDefault();
                  setShowLogin(true);
                }}
                className="bg-gray-900 text-white px-6 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-800 transition-colors"
              >
                Get started
              </motion.button>
            )}
          </div>
        </div>
      </nav>

      {/* Onboarding Modal */}
      {showOnboarding && (
        <Onboarding
          user={user}
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      {/* Profile Page - Separate full page */}
      {showProfile && user && (
        <Profile
          user={user}
          initialTab={profileInitialTab}
          onBack={() => {
            setShowProfile(false);
            setView('landing');
          }}
          onLogout={handleLogout}
          onProfileUpdated={(updatedUser) => setUser((prev) => (prev ? mergeProfileIntoUser(prev, updatedUser, true) : prev))}
        />
      )}

      {/* How to Use Page - Separate full page */}
      {!showProfile && currentPage === 'how-to-use' && (
        <HowToUse onBack={() => {
          setCurrentPage(null);
          setView('landing');
        }} />
      )}

      {/* Pricing Page - Separate full page */}
      {!showProfile && currentPage === 'pricing' && (
        <Pricing onBack={() => {
          setCurrentPage(null);
          setView('landing');
        }} />
      )}

      {/* Main App (hidden when profile or pages are shown) */}
      {!showProfile && !currentPage && (
      <main className="pt-20 min-h-[calc(100vh-80px)]">
        <AnimatePresence mode="wait">
          {/* Landing View */}
          {view === 'landing' && (
            <motion.div
              key="landing"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, y: -20, filter: 'blur(10px)' }}
              className="flex flex-col items-center justify-center min-h-[85vh] px-4 sm:px-6 relative overflow-hidden"
            >
              <div className="max-w-5xl w-full flex flex-col items-center text-center space-y-10 z-10">
                {/* Hero Title */}
                <motion.h1
                  variants={itemVariants}
                  className="text-6xl md:text-8xl font-bold tracking-tighter text-brand-primary max-w-4xl leading-[0.95]"
                >
                  Finding housing shouldn't be a
                  <span className="relative inline-block ml-4 text-[#9c795b] italic font-serif">
                    job.
                    <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#9c795b]/30" viewBox="0 0 100 10" preserveAspectRatio="none">
                      <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="none" />
                    </svg>
                  </span>
                </motion.h1>

                <motion.p
                  variants={itemVariants}
                  className="text-lg md:text-xl text-brand-primary/60 max-w-2xl font-medium"
                >
                  Find your perfect rental. Just ask.
                </motion.p>

                {/* Search Input with Scout Button */}
                <motion.div variants={itemVariants} className="w-full">
                  <AISearchNew
                    onSearchResults={(payload, query) => {
                      handleAISearchResults(payload, query);
                      setCurrentQuery(query || '');
                    }}
                    onFiltersApplied={handleAIFiltersApplied}
                    filters={filters}
                    resultCount={filteredProperties.length}
                    onSaveScout={handleSaveScout}
                    user={user}
                    onRequireLogin={() => setShowLogin(true)}
                    onRequireLocation={() => handleNavigateToProfile('profile')}
                    initialQuery={pendingQuery}
                  />
                </motion.div>
              </div>

              {/* Background Blobs */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
                <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-[#9c795b]/5 rounded-full blur-[100px]" />
                <div className="absolute bottom-[-10%] right-[-5%] w-[600px] h-[600px] bg-brand-primary/5 rounded-full blur-[100px]" />
              </div>
            </motion.div>
          )}

          {/* App Results View */}
          {view === 'app' && (
            <motion.div
              key="app"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="max-w-7xl mx-auto px-6 py-6 space-y-10"
            >
              {/* Page Header */}
              <div className="mb-8">
                <h1 className="text-4xl font-bold text-[#17252A] mb-2">Search Results</h1>
                <p className="text-[#17252A]/60">
                  {resultsSource === 'ai' && aiPagination.total > filteredProperties.length
                    ? `Showing ${filteredProperties.length} of ${aiPagination.total} properties`
                    : `Found ${filteredProperties.length} ${filteredProperties.length === 1 ? 'property' : 'properties'}`}
                </p>
              </div>

              {/* Top Controls */}
              <div className="mb-12 sticky top-24 z-30 bg-[#FAFAF9]/90 backdrop-blur-md py-4 -mx-4 px-4 rounded-b-2xl border-b border-[#9c795b]/5">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <motion.button
                      type="button"
                      whileHover={{ x: -3 }}
                      onClick={(e) => {
                        e.preventDefault();
                        setView('landing');
                      }}
                      className="p-2 hover:bg-[#17252A]/5 rounded-full transition-colors"
                    >
                      <ArrowRight className="w-5 h-5 rotate-180 text-[#17252A]/60" />
                    </motion.button>
                    <div className="text-2xl font-bold text-[#17252A] font-serif">
                      {currentQuery || 'Search Results'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Search Input in Results View */}
              <div className="mb-6">
                <AISearchNew
                  onSearchResults={(payload, query) => {
                    handleAISearchResults(payload, query);
                    setCurrentQuery(query || '');
                  }}
                  onFiltersApplied={handleAIFiltersApplied}
                  filters={filters}
                  resultCount={filteredProperties.length}
                  onSaveScout={handleSaveScout}
                  user={user}
                  onRequireLogin={() => setShowLogin(true)}
                  onRequireLocation={() => handleNavigateToProfile('profile')}
                  initialQuery={pendingQuery}
                />
              </div>

              {/* Filters (temporarily disabled) */}
              {/*
              <SearchFilters
                filters={filters}
                cities={cities}
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
                resultCount={filteredProperties.length}
                currencyPreference={user?.currency_preference}
                areaUnitPreference={user?.area_unit_preference}
              />
              */}

              {/* Results Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 pb-12 sm:pb-20">
                {loadingProperties && filteredProperties.length === 0 ? (
                  <div className="col-span-full flex items-center justify-center py-20">
                    <div className="flex flex-col items-center gap-4">
                      <Loader2 className="w-8 h-8 animate-spin text-[#9c795b]" />
                      <p className="text-sm text-gray-600">Loading properties...</p>
                    </div>
                  </div>
                ) : filteredProperties.length === 0 ? (
                  <div className="col-span-full">
                    <div className="bg-white border border-dashed border-[#9c795b]/30 rounded-2xl p-10 flex flex-col items-center justify-center text-center gap-4">
                      <div className="px-4 py-1 rounded-full bg-brand-light text-xs font-semibold text-brand-primary/70 uppercase tracking-wide">
                        No listings found
                      </div>
                      <h3 className="text-2xl font-semibold text-brand-primary">Let's help you find your next place.</h3>
                      <p className="text-sm text-brand-primary/60 max-w-md">
                        Try widening your budget, switching to a nearby city, or asking in plain language.
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          clearFilters();
                        }}
                        className="px-4 py-2 bg-[#9c795b] text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                ) : (
                  filteredProperties.map((property, index) => (
                    <motion.div
                      key={property.id || property.pineconeId || index}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <PropertyCard
                        property={property}
                        onClick={() => setSelectedProperty(property)}
                        isBlurred={!user}
                        onSignupPrompt={() => setShowLogin(true)}
                        currencyPreference={user?.currency_preference}
                        areaUnitPreference={user?.area_unit_preference}
                        fxRate={fxRate}
                      />
                    </motion.div>
                  ))
                )}
              </div>
              {resultsSource === 'ai' && (
                <div className="pb-16 flex flex-col items-center gap-3">
                  {isLoadingMoreAi && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Loader2 className="w-4 h-4 animate-spin text-[#9c795b]" />
                      Loading more properties...
                    </div>
                  )}
                  {!isLoadingMoreAi && aiPagination.hasMore && (
                    <button
                      type="button"
                      onClick={loadMoreAISearchResults}
                      className="px-4 py-2 bg-white border border-[#9c795b]/30 rounded-lg text-sm font-medium text-[#17252A] hover:bg-[#9c795b]/5 transition-colors"
                    >
                      Load more
                    </button>
                  )}
                  {!aiPagination.hasMore && filteredProperties.length > 0 && (
                    <p className="text-xs text-gray-500">You have reached the end of the results.</p>
                  )}
                  <div ref={loadMoreTriggerRef} className="h-2 w-full" />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      )}
    </div>
  );
}

export default App;
